// client/src/pages/Notifications.js (Client)

import React, { useEffect, useState } from "react";
import "./Notifications.css";
import "./Dashboard.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import notificationsImage from "../assets/notifications.jpg";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const toE164 = (raw) => {
  const s = String(raw || "").trim();
  const digits = s.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits.startsWith("+") ? digits : `+${digits}`;
};

const LS_EMAIL_UPDATES = "pref_emailUpdates";
const LS_SMS_UPDATES = "pref_smsUpdates";
const LS_MOBILE = "pref_mobile";

const Notifications = () => {
  const { user, email: authEmail, updatePreferredDisplayName } = useAuth();
  const navigate = useNavigate();

  const [isEmailChecked, setIsEmailChecked] = useState(true);
  const [isSMSChecked, setIsSMSChecked] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [saveError, setSaveError] = useState("");

  const BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5000").replace(
    /\/+$/,
    ""
  );

  const handleLogoClick = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    const lsEmailUpdates = localStorage.getItem(LS_EMAIL_UPDATES);
    const lsSmsUpdates = localStorage.getItem(LS_SMS_UPDATES);
    const lsMobile = localStorage.getItem(LS_MOBILE);
    const lsPreferredName = localStorage.getItem("preferredDisplayName");

    if (lsEmailUpdates !== null) setIsEmailChecked(lsEmailUpdates === "true");
    if (lsSmsUpdates !== null) setIsSMSChecked(lsSmsUpdates === "true");
    if (lsMobile) setMobileNumber(lsMobile);
    if (lsPreferredName) setPreferredName(lsPreferredName);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      setNotice("");

      try {
        const token =
          localStorage.getItem("clientAuthToken") ||
          localStorage.getItem("authToken");

        const res = await fetch(`${BASE}/api/auth/profile`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        let data = {};
        try {
          data = await res.json();
        } catch {
          // ignore JSON parse errors
        }

        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

        const resolvedEmail =
          data?.email ||
          authEmail ||
          user?.email ||
          localStorage.getItem("userEmail") ||
          localStorage.getItem("email") ||
          "N/A";

        const serverMobile = data?.mobile || "";
        const nextPreferredName =
          data?.preferredDisplayName ||
          data?.user?.preferredDisplayName ||
          "";

        setEmail(resolvedEmail);
        setMobileNumber(serverMobile);
        setPreferredName(nextPreferredName);

        localStorage.setItem(LS_MOBILE, serverMobile);

        if (typeof data?.emailUpdates === "boolean") {
          setIsEmailChecked(data.emailUpdates);
          localStorage.setItem(LS_EMAIL_UPDATES, String(data.emailUpdates));
        }

        if (typeof data?.smsUpdates === "boolean") {
          setIsSMSChecked(data.smsUpdates);
          localStorage.setItem(LS_SMS_UPDATES, String(data.smsUpdates));
        }

        if (nextPreferredName) {
          localStorage.setItem("preferredDisplayName", nextPreferredName);
        } else {
          localStorage.removeItem("preferredDisplayName");
        }

        setShowPopup(false);
      } catch (err) {
        console.error("[Client Notifications] Fetch failed:", err);
        setError(err.message || "Failed to fetch notification preferences.");
        setEmail(
          authEmail ||
            user?.email ||
            localStorage.getItem("userEmail") ||
            localStorage.getItem("email") ||
            "N/A"
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [BASE, authEmail, user]);

  useEffect(() => {
    if (!isSMSChecked) setShowPopup(false);
  }, [isSMSChecked]);

  const validate = () => {
    if (!email || email === "N/A") return "A valid account email is required.";
    if (isSMSChecked) {
      const digits = String(mobileNumber || "").replace(/\D/g, "");
      if (digits.length < 10) {
        return "Please enter a valid mobile number (at least 10 digits).";
      }
    }
    return "";
  };

  const handleSavePreferences = async () => {
    setSaveError("");
    setNotice("");

    const msg = validate();
    if (msg) {
      setSaveError(msg);
      return;
    }

    const token =
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("authToken");
    const normalizedMobile = isSMSChecked ? toE164(mobileNumber) : null;
    const trimmedPreferredName = preferredName.trim();

    const payload = {
      emailUpdates: !!isEmailChecked,
      smsUpdates: !!isSMSChecked,
      mobile: normalizedMobile,
      preferredDisplayName: trimmedPreferredName,
    };

    try {
      setSaving(true);
      const res = await fetch(`${BASE}/api/users/notification-preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        // ignore JSON parse errors
      }

      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      const nextPreferredName = data?.preferredDisplayName || "";
      const nextMobile =
        typeof data?.mobile === "string" ? data.mobile : normalizedMobile || "";
      const nextEmailUpdates =
        typeof data?.emailUpdates === "boolean"
          ? data.emailUpdates
          : !!isEmailChecked;
      const nextSmsUpdates =
        typeof data?.smsUpdates === "boolean" ? data.smsUpdates : !!isSMSChecked;

      setPreferredName(nextPreferredName);
      setMobileNumber(nextMobile);
      setIsEmailChecked(nextEmailUpdates);
      setIsSMSChecked(nextSmsUpdates);

      localStorage.setItem(LS_EMAIL_UPDATES, String(nextEmailUpdates));
      localStorage.setItem(LS_SMS_UPDATES, String(nextSmsUpdates));
      localStorage.setItem(LS_MOBILE, nextMobile);

      if (nextPreferredName) {
        localStorage.setItem("preferredDisplayName", nextPreferredName);
      } else {
        localStorage.removeItem("preferredDisplayName");
      }

      if (typeof updatePreferredDisplayName === "function") {
        updatePreferredDisplayName(nextPreferredName);
      }

      setShowPopup(false);
      setSaveError("");
      setNotice("Preferences saved successfully.");
      setTimeout(() => setNotice(""), 3000);
    } catch (err) {
      console.error("[Client Notifications] Save failed:", err);
      setSaveError(err.message || "Failed to save preferences.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
    setSaveError("");
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSaveError("");
  };

  return (
    <div className="core-dashboard notifications-page">
      <div className="core-dashboard-content notifications-page-content cortex-page-intro-shell">
        <header className="notifications-header">
          <CphereHeaderLogo
            alt="Powered by Cphere"
            className="notifications-header-logo"
            onClick={handleLogoClick}
          />

          <div className="notifications-hero-container">
            <div className="notifications-hero-pill">
              <span className="notifications-hero-pill-label cphere-hero-pill-wordmark">
                Alert
              </span>
              <span className="notifications-hero-pill-tag cphere-hero-pill-tag">
                Preferences
              </span>
            </div>
            <div className="notifications-hero-shimmer-bar" />
          </div>
        </header>

        <div className="notifications-content">
          <div className="notifications-card">
            <div className="notifications-hero-wrapper">
              <img
                src={notificationsImage}
                alt="Notifications"
                className="notifications-hero-img"
              />
            </div>

            {loading ? (
              <p className="settings-message">Loading...</p>
            ) : (
              <form onSubmit={(e) => e.preventDefault()}>
                {error && <p className="settings-message error">{error}</p>}

                <div className="notification-preferred-name-block">
                  <label
                    className="notification-preferred-name-label"
                    htmlFor="client-preferred-name"
                  >
                    Preferred Name
                  </label>
                  <input
                    id="client-preferred-name"
                    className="notification-preferred-name-input"
                    type="text"
                    value={preferredName}
                    onChange={(e) => setPreferredName(e.target.value)}
                    placeholder="Preferred Name"
                    maxLength={60}
                  />
                  <p className="notification-preferred-name-help">
                    Used by Eden voice and notifications.
                  </p>
                </div>

                <div className="notification-option">
                  <label className="notification-label">
                    <input
                      type="checkbox"
                      checked={isEmailChecked}
                      onChange={() => setIsEmailChecked((v) => !v)}
                    />
                    <span>EMAIL UPDATES</span>
                  </label>
                  <p className="notification-info notification-info-email">
                    <strong>EMAIL:</strong> {email || "N/A"}
                  </p>
                </div>

                <div className="notification-option">
                  <label className="notification-label">
                    <input
                      type="checkbox"
                      checked={isSMSChecked}
                      onChange={() => setIsSMSChecked((v) => !v)}
                    />
                    <span>SMS UPDATES</span>
                  </label>

                  <p className="notification-info">
                    <strong>MOBILE:</strong>{" "}
                    {mobileNumber ? mobileNumber : "N/A"}
                  </p>

                  {isSMSChecked && (
                    <button
                      type="button"
                      className="edit-password-button"
                      onClick={handleOpenPopup}
                    >
                      Edit Mobile
                    </button>
                  )}
                </div>

                {notice && <p className="settings-message success">{notice}</p>}

                <button
                  type="button"
                  className="notifications-save-button"
                  onClick={handleSavePreferences}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Preferences"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <div className="settings-popup-hero-wrapper">
              <img
                src={notificationsImage}
                alt="Notifications"
                className="settings-popup-hero-img"
              />
            </div>

            <h2>Edit Mobile</h2>

            <div className="popup-content">
              <label>Mobile Number:</label>
              <input
                type="tel"
                value={mobileNumber || ""}
                onChange={(e) => setMobileNumber(e.target.value)}
                placeholder="Enter mobile number"
              />

              {isSMSChecked && !mobileNumber && (
                <div className="notification-warning">
                  Please enter your mobile number before saving to enable SMS
                  updates.
                </div>
              )}

              {saveError && <p className="error-message">{saveError}</p>}

              <div className="form-buttons">
                <button type="button" onClick={handleSavePreferences} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={handleClosePopup}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
